import React, { useMemo } from 'react';
import { Box, ButtonBase, MenuItem, Select, Typography } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';

const ELLIPSIS = 'ellipsis';

function range(start, end) {
    const out = [];
    for (let i = start; i <= end; i += 1) out.push(i);
    return out;
}

function buildPageItems(page, totalPages, siblingCount = 1) {
    const totalSlots = siblingCount * 2 + 5;
    if (totalPages <= totalSlots) return range(1, totalPages);

    const leftSibling = Math.max(page - siblingCount, 1);
    const rightSibling = Math.min(page + siblingCount, totalPages);
    const showLeftEllipsis = leftSibling > 2;
    const showRightEllipsis = rightSibling < totalPages - 1;
    const edgeCount = 3 + 2 * siblingCount;

    if (!showLeftEllipsis && showRightEllipsis) {
        return [...range(1, edgeCount), ELLIPSIS, totalPages];
    }
    if (showLeftEllipsis && !showRightEllipsis) {
        return [1, ELLIPSIS, ...range(totalPages - edgeCount + 1, totalPages)];
    }
    return [1, ELLIPSIS, ...range(leftSibling, rightSibling), ELLIPSIS, totalPages];
}

const cellBase = {
    minWidth: 38,
    height: 38,
    px: 1,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1,
    color: 'text.primary',
    backgroundColor: 'background.paper',
    transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' },
    '&.Mui-focusVisible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: 2,
    },
};

function NavButton({ label, onClick, disabled, children }) {
    return (
        <ButtonBase
            aria-label={label}
            title={label}
            onClick={onClick}
            disabled={disabled}
            sx={{
                ...cellBase,
                color: 'text.secondary',
                '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed' },
                '& svg': { fontSize: 20 },
            }}
        >
            {children}
        </ButtonBase>
    );
}

/**
 * Reusable table/list pager: rows-per-page selector, an "X–Y of N" total readout,
 * and numbered page buttons with first/prev/next/last controls (see mockup).
 *
 * @param count total number of items (not pages)
 * @param page  1-based current page
 * @param pageSize current page size
 * @param onPageChange (nextPage1Based) => void
 * @param onPageSizeChange (nextSize) => void
 */
function Pagination({
    count,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    itemLabel,
    rowsPerPageOptions = [10, 20, 50, 100],
}) {
    const { t } = useTranslation();
    const totalPages = Math.max(1, Math.ceil(count / pageSize));
    const current = Math.min(Math.max(1, page), totalPages);

    const items = useMemo(() => buildPageItems(current, totalPages), [current, totalPages]);

    const from = count === 0 ? 0 : (current - 1) * pageSize + 1;
    const to = Math.min(count, current * pageSize);

    const summaryText = itemLabel
        ? t('pagination.summaryItems', 'Showing {{from}} to {{to}} of {{total}} {{item}}', { from, to, total: count, item: itemLabel })
        : t('pagination.summary', 'Showing {{from}} to {{to}} of {{total}}', { from, to, total: count });

    const goTo = (next) => {
        const clamped = Math.min(Math.max(1, next), totalPages);
        if (clamped !== current) onPageChange(clamped);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {summaryText}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    size="small"
                    aria-label={t('pagination.rowsPerPage', 'Rows per page')}
                    sx={{
                        height: 38,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' },
                    }}
                >
                    {rowsPerPageOptions.map((n) => (
                        <MenuItem key={n} value={n}>
                            {t('pagination.perPage', '{{size}} per page', { size: n })}
                        </MenuItem>
                    ))}
                </Select>

                <Box component="nav" aria-label={t('pagination.label', 'Pagination')} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <NavButton label={t('pagination.first', 'First page')} onClick={() => goTo(1)} disabled={current <= 1}>
                    <FirstPageIcon />
                </NavButton>
                <NavButton label={t('pagination.previous', 'Previous page')} onClick={() => goTo(current - 1)} disabled={current <= 1}>
                    <ChevronLeftIcon />
                </NavButton>

                {items.map((item, index) =>
                    item === ELLIPSIS ? (
                        <Box
                            key={`ellipsis-${index}`}
                            aria-hidden
                            sx={{
                                minWidth: 38,
                                height: 38,
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                pb: 0.5,
                                color: 'text.disabled',
                                userSelect: 'none',
                            }}
                        >
                            …
                        </Box>
                    ) : (
                        <ButtonBase
                            key={item}
                            onClick={() => goTo(item)}
                            aria-label={t('pagination.goToPage', 'Go to page {{page}}', { page: item })}
                            aria-current={item === current ? 'page' : undefined}
                            sx={{
                                ...cellBase,
                                ...(item === current && {
                                    backgroundColor: 'primary.main',
                                    borderColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': { backgroundColor: 'primary.dark', borderColor: 'primary.dark' },
                                }),
                            }}
                        >
                            {item}
                        </ButtonBase>
                    )
                )}

                <NavButton label={t('pagination.next', 'Next page')} onClick={() => goTo(current + 1)} disabled={current >= totalPages}>
                    <ChevronRightIcon />
                </NavButton>
                <NavButton label={t('pagination.last', 'Last page')} onClick={() => goTo(totalPages)} disabled={current >= totalPages}>
                    <LastPageIcon />
                </NavButton>
                </Box>
            </Box>
        </Box>
    );
}

export default Pagination;
